import { supabase } from '../lib/supabase';

// Normalize error to always be a proper Error instance
function normalizeError(err, fallback) {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const msg = err?.message || fallback || 'Unknown error';
    return Object.assign(new Error(msg), err);
  }
  return new Error(err || fallback || 'Unknown error');
}

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Compute the automated status for a resource based on its assignments.
 * Rules:
 *   - If DB status is 'Maintenance' → keep Maintenance (manual override)
 *   - If resource has any assignment whose date range overlaps the next 30 days → Assigned
 *   - Otherwise → Available
 */
export function computeResourceStatus(dbStatus, assignments = []) {
  if (dbStatus === 'Maintenance') return 'Maintenance';

  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  const in30Days = new Date(today);
  in30Days?.setDate(in30Days?.getDate() + 30);
  in30Days?.setHours(23, 59, 59, 999);

  const hasAssignmentIn30 = assignments?.some((a) => {
    const startRaw = a?.start_date || a?.startDate;
    const endRaw = a?.end_date || a?.endDate;
    if (!startRaw || !endRaw) return false;
    const start = new Date(startRaw);
    const end = new Date(endRaw);
    // Overlaps next 30 days: assignment starts before 30-day window ends AND ends after today
    return start <= in30Days && end >= today;
  });

  return hasAssignmentIn30 ? 'Assigned' : 'Available';
}

export const resourceService = {
  // Get all resources with assignments
  async getAll() {
    try {
      // Fetch resources with direct resource_assignments
      const { data: resourcesData, error: resourcesError } = await supabase?.from('resources')?.select(`
          *,
          assignments:resource_assignments(*)
        `)?.order('created_at', { ascending: false });

      if (resourcesError) throw normalizeError(resourcesError);

      // Also fetch deliverable-based assignments (resources assigned to deliverables which have dates)
      // Use a simpler query without nested project join to avoid RLS issues
      let deliverableAssignmentsByResource = {};
      try {
        const { data: deliverableAssignments, error: deliverableError } = await supabase
          ?.from('deliverable_resource_assignments')
          ?.select(`
            id,
            resource_id,
            start_date,
            end_date,
            assigned_at,
            deliverable:deliverables(id, name, start_date, end_date, project_id, status)
          `);

        if (!deliverableError && deliverableAssignments) {
          // Collect unique project_ids to fetch project names separately
          const projectIds = [...new Set(
            deliverableAssignments
              ?.map(da => da?.deliverable?.project_id)
              ?.filter(Boolean)
          )];

          // Fetch project names if we have project IDs
          let projectMap = {};
          if (projectIds?.length > 0) {
            try {
              const { data: projectsData } = await supabase
                ?.from('projects')
                ?.select('id, project_name, project_number')
                ?.in('id', projectIds);
              if (projectsData) {
                projectsData?.forEach(p => {
                  projectMap[p.id] = p?.project_name || p?.project_number || 'Project';
                });
              }
            } catch (_) {
              // project names unavailable, use fallback
            }
          }

          deliverableAssignments?.forEach(da => {
            const rid = da?.resource_id;
            if (!rid) return;
            if (!deliverableAssignmentsByResource?.[rid]) deliverableAssignmentsByResource[rid] = [];
            const deliverable = da?.deliverable;
            // Use the resource's own allocation dates; fall back to deliverable dates if not set
            const assignmentStartDate = da?.start_date || deliverable?.start_date;
            const assignmentEndDate = da?.end_date || deliverable?.end_date;
            if (assignmentStartDate && assignmentEndDate) {
              const today = new Date();
              const start = new Date(assignmentStartDate);
              const end = new Date(assignmentEndDate);
              const projectName = deliverable?.project_id
                ? (projectMap?.[deliverable?.project_id] || 'Project')
                : 'Project';
              deliverableAssignmentsByResource?.[rid]?.push({
                id: `del-${da?.id}`,
                resource_id: rid,
                project_id: deliverable?.project_id,
                project_name: projectName,
                phase_name: deliverable?.name || null,
                start_date: assignmentStartDate,
                end_date: assignmentEndDate,
                is_current: start <= today && end >= today,
                is_upcoming: start > today,
                source: 'deliverable'
              });
            }
          });
        }
      } catch (_deliverableErr) {
        // Deliverable assignments fetch failed — continue with direct assignments only
        console.warn('Could not fetch deliverable assignments:', _deliverableErr?.message);
      }

      // Merge deliverable assignments into each resource's assignments array
      const mergedData = (resourcesData || [])?.map(resource => {
        const directAssignments = resource?.assignments || [];
        const deliverableBased = deliverableAssignmentsByResource?.[resource?.id] || [];
        // Avoid duplicates: only add deliverable assignments not already covered by a direct assignment
        const combined = [...directAssignments, ...deliverableBased];
        // Auto-compute status: Maintenance is preserved, otherwise Assigned/Available based on 30-day window
        const computedStatus = computeResourceStatus(resource?.status, combined);
        return { ...resource, assignments: combined, status: computedStatus };
      });

      return { data: toCamelCase(mergedData), error: null };
    } catch (error) {
      console.error('Error fetching resources:', error?.message);
      return { data: null, error };
    }
  },

  // Get single resource by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('resources')?.select(`
          *,
          assignments:resource_assignments(*)
        `)?.eq('id', id)?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching resource:', error?.message);
      return { data: null, error };
    }
  },

  // Get resources by category
  async getByCategory(category) {
    try {
      const { data, error } = await supabase?.from('resources')?.select(`
          *,
          assignments:resource_assignments(*)
        `)?.eq('category', category)?.order('name', { ascending: true });

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching resources by category:', error?.message);
      return { data: null, error };
    }
  },

  // Create new resource
  async create(resourceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Auto-generate identifier from name + random suffix if not provided
      const identifier = resourceData?.identifier ||
        `${(resourceData?.name || 'resource')?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/^-|-$/g, '')}-${Math.random()?.toString(36)?.slice(2, 7)}`;

      const snakeData = toSnakeCase({ ...resourceData, identifier, createdBy: user?.id });
      const { data, error } = await supabase?.from('resources')?.insert(snakeData)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating resource:', error?.message);
      return { data: null, error };
    }
  },

  // Update resource
  async update(id, resourceData) {
    try {
      const snakeData = toSnakeCase(resourceData);
      const { data, error } = await supabase?.from('resources')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating resource:', error?.message);
      return { data: null, error };
    }
  },

  // Delete resource
  async delete(id) {
    try {
      const { error } = await supabase?.from('resources')?.delete()?.eq('id', id);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error deleting resource:', error?.message);
      return { error };
    }
  },

  // Check resource availability for a date range
  async checkAvailability(resourceId, startDate, endDate, excludeAssignmentId = null) {
    try {
      let query = supabase
        ?.from('resource_assignments')
        ?.select('*')
        ?.eq('resource_id', resourceId)
        ?.lte('start_date', endDate)
        ?.gte('end_date', startDate);

      if (excludeAssignmentId) {
        query = query?.neq('id', excludeAssignmentId);
      }

      const { data, error } = await query;
      if (error) throw normalizeError(error);

      const conflicts = toCamelCase(data || []);
      return {
        available: conflicts?.length === 0,
        conflicts,
        error: null
      };
    } catch (error) {
      console.error('Error checking availability:', error?.message);
      return { available: false, conflicts: [], error };
    }
  },

  // Detect scheduling conflicts for a list of resources over a date range
  async detectConflicts(resourceIds, startDate, endDate) {
    try {
      if (!resourceIds?.length) return { conflicts: [], error: null };

      const { data, error } = await supabase
        ?.from('resource_assignments')
        ?.select(`
          *,
          resource:resources(id, name, category)
        `)
        ?.in('resource_id', resourceIds)
        ?.lte('start_date', endDate)
        ?.gte('end_date', startDate);

      if (error) throw normalizeError(error);

      const assignments = toCamelCase(data || []);

      // Group by resource and find overlaps
      const byResource = {};
      assignments?.forEach(a => {
        const rid = a?.resourceId;
        if (!byResource?.[rid]) byResource[rid] = [];
        byResource?.[rid]?.push(a);
      });

      const conflicts = [];
      Object.entries(byResource)?.forEach(([resourceId, resourceAssignments]) => {
        if (resourceAssignments?.length > 1) {
          conflicts?.push({
            resourceId,
            resourceName: resourceAssignments?.[0]?.resource?.name,
            resourceCategory: resourceAssignments?.[0]?.resource?.category,
            overlappingAssignments: resourceAssignments
          });
        }
      });

      return { conflicts, error: null };
    } catch (error) {
      console.error('Error detecting conflicts:', error?.message);
      return { conflicts: [], error };
    }
  },

  // Get all assignments with resource and project details
  async getAllAssignments(filters = {}) {
    try {
      let query = supabase
        ?.from('resource_assignments')
        ?.select(`
          *,
          resource:resources(id, name, category, status)
        `)
        ?.order('start_date', { ascending: true });

      if (filters?.resourceId) {
        query = query?.eq('resource_id', filters?.resourceId);
      }
      if (filters?.projectId) {
        query = query?.eq('project_id', filters?.projectId);
      }
      if (filters?.startDate) {
        query = query?.gte('start_date', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('end_date', filters?.endDate);
      }

      const { data, error } = await query;
      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching all assignments:', error?.message);
      return { data: null, error };
    }
  },

  // Get resource assignments
  async getAssignments(resourceId) {
    try {
      const { data, error } = await supabase?.from('resource_assignments')?.select('*')?.eq('resource_id', resourceId)?.order('start_date', { ascending: true });

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching assignments:', error?.message);
      return { data: null, error };
    }
  },

  // Create assignment
  async createAssignment(assignmentData) {
    try {
      const snakeData = toSnakeCase(assignmentData);
      const { data, error } = await supabase?.from('resource_assignments')?.insert(snakeData)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating assignment:', error?.message);
      return { data: null, error };
    }
  },

  // Update assignment
  async updateAssignment(id, assignmentData) {
    try {
      const snakeData = toSnakeCase(assignmentData);
      const { data, error } = await supabase?.from('resource_assignments')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating assignment:', error?.message);
      return { data: null, error };
    }
  },

  // Delete assignment
  async deleteAssignment(id) {
    try {
      const { error } = await supabase?.from('resource_assignments')?.delete()?.eq('id', id);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error deleting assignment:', error?.message);
      return { error };
    }
  },

  // Unassign all resources from a project (both direct and deliverable-based assignments)
  async unassignAllFromProject(projectId) {
    try {
      // Remove direct resource assignments for this project
      const { error: assignError } = await supabase
        ?.from('resource_assignments')
        ?.delete()
        ?.eq('project_id', projectId);

      if (assignError) throw normalizeError(assignError);

      // Remove deliverable-based resource assignments for deliverables in this project
      const { data: deliverables } = await supabase
        ?.from('deliverables')
        ?.select('id')
        ?.eq('project_id', projectId);

      if (deliverables?.length > 0) {
        const deliverableIds = deliverables?.map(d => d?.id);
        const { error: delAssignError } = await supabase
          ?.from('deliverable_resource_assignments')
          ?.delete()
          ?.in('deliverable_id', deliverableIds);

        if (delAssignError) throw normalizeError(delAssignError);
      }

      return { error: null };
    } catch (error) {
      console.error('Error unassigning all resources from project:', error?.message);
      return { error };
    }
  },

  // Get resource utilization stats
  async getUtilizationStats() {
    try {
      const { data: resources, error: resError } = await supabase
        ?.from('resources')
        ?.select('id, name, category, status');
      if (resError) throw normalizeError(resError);

      const today = new Date()?.toISOString()?.split('T')?.[0];
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

      const { data: assignments, error: assignError } = await supabase
        ?.from('resource_assignments')
        ?.select('resource_id, start_date, end_date')
        ?.gte('end_date', thirtyDaysAgo)
        ?.lte('start_date', today);
      if (assignError) throw normalizeError(assignError);

      const resourceMap = {};
      (resources || [])?.forEach(r => {
        resourceMap[r?.id] = { ...r, assignmentCount: 0, totalHours: 0 };
      });

      (assignments || [])?.forEach(a => {
        if (resourceMap?.[a?.resource_id]) {
          resourceMap[a?.resource_id].assignmentCount += 1;
          resourceMap[a?.resource_id].totalHours += 8;
        }
      });

      const stats = Object.values(resourceMap);
      const byCategory = {};
      stats?.forEach(r => {
        if (!byCategory?.[r?.category]) byCategory[r?.category] = { total: 0, inUse: 0, available: 0 };
        byCategory[r?.category].total += 1;
        if (r?.status === 'In Use') byCategory[r?.category].inUse += 1;
        if (r?.status === 'Available') byCategory[r?.category].available += 1;
      });

      return {
        data: {
          resources: toCamelCase(stats),
          byCategory,
          total: stats?.length,
          inUse: stats?.filter(r => r?.status === 'In Use')?.length,
          available: stats?.filter(r => r?.status === 'Available')?.length,
          maintenance: stats?.filter(r => r?.status === 'Maintenance')?.length
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching utilization stats:', error?.message);
      return { data: null, error };
    }
  },

  // Subscribe to real-time resource changes
  subscribeToChanges(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('resources_realtime')
      ?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'resources' },
        (payload) => onInsert && onInsert(toCamelCase(payload?.new))
      )
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'resources' },
        (payload) => onUpdate && onUpdate(toCamelCase(payload?.new))
      )
      ?.on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'resources' },
        (payload) => onDelete && onDelete(payload?.old?.id)
      )
      ?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) supabase?.removeChannel(channel);
  }
};